import { Link } from "react-router-dom";
import { useDateFormatter, useTranslations } from "../../contexts/Translation";
import { categoryLabel } from "../Dashboard/labels";
import EmScore from "../Survey/EmScore";
import { movementSummary, SurveyRow } from "./goalSurveys";
import styles from "./Details.module.css";

type SurveyTableProps = {
  rows: SurveyRow[];
  categories: string[];
  /** False for rows that are periods, not surveys: the name is shown without a link. */
  linkRows?: boolean;
};

const Diff = ({ value }: { value?: number }) => {
  if (value === undefined) {
    return null;
  }
  const className = Math.abs(value) < 0.05 ? styles.diffNeutral : value > 0 ? styles.diffPositive : styles.diffNegative;
  return (
    <span className={[styles.diff, className].join(" ")}>
      {value > 0 ? "+" : ""}
      {value.toFixed(1)} {Math.abs(value) < 0.05 ? "→" : value > 0 ? "↗" : "↘"}
    </span>
  );
};

const SurveyTable = ({ rows, categories, linkRows = true }: SurveyTableProps) => {
  const t = useTranslations("improvementGoals");
  const d = useDateFormatter();
  const summary = movementSummary(rows, categories);
  const names = (list: string[]) => list.map((c) => t(categoryLabel(c))).join(", ");

  return (
    <section className={styles.card}>
      <div className={styles.tableScroll}>
        <table className={styles.table}>
          <caption className={styles.cardHeading}>{t("latestSurveys")}</caption>
          <thead>
            <tr>
              <th scope='col'>{t("colSurvey")}</th>
              <th scope='col'>{t("colLastResponse")}</th>
              <th scope='col'>{t("colRespondents")}</th>
              {categories.map((c) => (
                <th scope='col' key={c}>
                  {t(categoryLabel(c))}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id}>
                <th scope='row'>
                  {linkRows ? <Link to={`/survey/${row.id}`}>{row.surveyName || t("originalSurvey")}</Link> : row.surveyName}
                  {row.isBaseline && <span className={styles.tag}>{t("baseline")}</span>}
                  {row.isOpen && <span className={styles.tag}>{t("open")}</span>}
                </th>
                <td>{d(row.changed)}</td>
                <td>{row.respondents ?? ""}</td>
                {categories.map((c) => (
                  <td key={c}>
                    <span className={styles.scoreCell}>
                      <EmScore labelVariant='compact' size={2} status={row.isOpen ? "ongoing" : undefined} score={row.scores[c]} />
                      <Diff value={row.diff[c]} />
                    </span>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {summary.key === "noRecentSuveys" ? (
        <p className={styles.text}>
          {t("noRecentSuveys")} <Link to='/survey'>{t("createSurvey")}</Link>
        </p>
      ) : (
        <p className={styles.text}>
          {[
            summary.up.length > 0 && t("movementUp", names(summary.up)),
            summary.down.length > 0 && t("movementDown", names(summary.down)),
            summary.same.length > 0 && t("movementSame", names(summary.same)),
            summary.openCount > 0 && t("movementOpen", summary.openCount),
          ]
            .filter(Boolean)
            .join(" ")}
        </p>
      )}
    </section>
  );
};

export default SurveyTable;
